import React, { useMemo } from 'react';
import { Message } from 'features/ai-assistant/components';
import { SENDER_TYPE } from 'features/ai-assistant/constants';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { UIStatus } from 'UI/constants/status';
import { SvgNoActivities } from 'UI/res';

import { useScrollToUserMessage } from './ConversationMessages.hooks';
import { useConversationMessagesStyles } from './ConversationMessages.styled';

const COPIES = strings.ai.generators.assistant;

export const ConversationMessages = ({
  isLoadingMoreMessages,
  messages,
  messageFullWidth,
  metaDataComponent,
  onFetchMoreMessages,
  onFinishScrollDown,
  scrollConfig,
  uiStatus
}) => {
  const isLoading = uiStatus === UIStatus.Loading;
  const isSending = uiStatus === UIStatus.Sending;
  const classes = useConversationMessagesStyles();

  const handleScrollToTop = () => {
    if (isLoading || isLoadingMoreMessages) return;

    onFetchMoreMessages();
  };

  const { containerRef, lastUserMessageRef } = useScrollToUserMessage({
    messagesLength: messages.length,
    onFinishScroll: onFinishScrollDown,
    onScrollTop: handleScrollToTop,
    scrollConfig
  });

  const safeMessages = useMemo(() => {
    if (!Array.isArray(messages)) {
      return [];
    }
    return messages.slice().reverse();
  }, [messages]);

  const latestUserMessageIndex = useMemo(() => {
    return safeMessages.reduce(
      (lastIndex, message, index) => (message.senderType === SENDER_TYPE.user ? index : lastIndex),
      -1
    );
  }, [safeMessages]);

  const isEmpty = safeMessages.length === 0;

  return (
    <div className={classes.conversationContainer} ref={containerRef}>
      <When condition={isEmpty && !isLoading && !isSending}>
        <EmptyPlaceholder
          customEmptyState={<SvgNoActivities height={200} />}
          title={COPIES.emptyConversation.title}
          subtitle={COPIES.emptyConversation.subtitle}
        />
      </When>

      {safeMessages.map((message, index) => (
        <div
          key={message?.createdAt || message?.id}
          ref={index === latestUserMessageIndex ? lastUserMessageRef : null}
        >
          <Message
            message={message}
            messageFullWidth={messageFullWidth}
            metaDataComponent={metaDataComponent}
          />
        </div>
      ))}
    </div>
  );
};
